import InvoiceIcon from "@assets/images/invoices.svg";
import PointIcon from "@assets/images/location-marker.svg";
import ShippingIcon from "@assets/images/shipping-icon.svg";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid
} from "@material-ui/core";
import DataTable from "@saleor/changeOrder/components/DataSortTable";
import MyTypography from "@saleor/components/MyTypography";
import { makeStyles } from "@saleor/macaw-ui";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { CustomerContext } from "../../CollectionFormSubmit";
import { ShippingTypeEnum } from "../Shipping";

export interface ModalShippingProps {
  onClose?: () => void;
  visible?: boolean;
  type: ShippingTypeEnum;
  address: any[];
  handleApplyShipping: (value: string) => void;
  initialValue?: string;
  addressOptions?: any[];
}

export const useStyles = makeStyles(
  theme => ({
    dialogTitle: {
      textAlign: "left",
      padding: "0",
      display: "flex"
    },
    mainTitle: {
      fontSize: "2rem",
      color: theme.palette.grey[900],
      fontWeight: 700
    },
    subTitle: {
      color: theme.palette.info.main,
      fontWeight: 400,
      fontSize: "1.6rem"
    },
    closeDialog: {
      cursor: "pointer"
    },
    rowlength: {
      color: "#009FB8",
      fontFamily: "SCG",
      fontSize: "20px",
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "26px"
    },
    root: {
      "& .MuiPaper-root": {
        maxWidth: 600,
        width: "100%",
        borderRadius: 12,
        padding: " 24px 32px"
      },
      "& .MuiDialogTitle-root": {
        padding: "0",
        display: "flex"
      },
      "& .MuiDialogActions-root": {
        borderTop: 0,
        paddingBottom: 24
      },
      "& .MuiFormControlLabel-label": {
        fontSize: "1.4rem",
        lineHeight: "22px",
        color: theme.palette.grey[900]
      },
      "& .MuiRadio-root svg": {
        width: 18,
        height: 18
      }
    },
    nameHead: {
      marginTop: 0,
      marginLeft: "12px",
      display: "flex !important",
      h2: {
        display: "flex !important"
      }
    },
    dialogTitleWrap: {
      height: "40px",
      display: " flex",
      alignItems: "center",
      lineHeight: "normal",
      marginBottom: "24px",
      marginTop: "0px"
    },
    detailTitle: {
      fontWeight: "bold",
      marginBottom: 10,
      "& .normalText": {
        fontWeight: "normal"
      }
    },
    dialogContent: {
      maxHeight: "calc(100% - 64px)",
      overflow: "auto",
      padding: "0 !important",
      "&::-webkit-scrollbar": {
        height: "14px",
        background: "#FFFFFF"
      },
      "&::-webkit-scrollbar-track": {
        borderRadius: 10
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#CCCCCC",
        borderRadius: 10,
        border: "4px solid #FFFFFF",
        height: "6px"
      }
    },
    buttonAction: {
      width: "50%",
      height: 48
    }
  }),
  { name: "ModalShipping" }
);

const ModalShipping: React.FC<ModalShippingProps> = props => {
  const {
    visible,
    onClose,
    type,
    address,
    handleApplyShipping,
    addressOptions,
    initialValue
  } = props;
  const { formik } = useContext(CustomerContext);
  const [shipping, setShipping] = useState<string>(initialValue);
  const [listAddress, setListAddress] = useState<
    Array<{
      address: string;
      partnerNo: string;
      partnerName: string;
    }>
  >([]);
  const classes = useStyles();
  const intl = useIntl();
  let fieldName;
  if (type === ShippingTypeEnum.Delivery) {
    fieldName = "shipTo";
  }
  if (type === ShippingTypeEnum.TaxInvoices) {
    fieldName = "billTo";
  }
  const [selectedRow, setSelectedRow] = useState(
    formik?.values?.[fieldName]?.split("-")[0]?.trim()
  );
  const getFormattedAddress = address => {
    const parts = address?.split("\n");
    return parts.length > 1 ? parts[1].trim() : address;
  };

  const rows = useMemo(() => {
    switch (type) {
      case ShippingTypeEnum.TaxInvoices:
        return listAddress.map(shippingAddress => ({
          BilltoCode: shippingAddress.partnerNo,
          billingAddress: (
            <>
              <strong>{shippingAddress.partnerName}</strong>
              <br />
              {getFormattedAddress(shippingAddress.address)}
            </>
          )
        }));
      case ShippingTypeEnum.Delivery:
      default:
        return listAddress.map(shippingAddress => ({
          ShiptoCode: shippingAddress.partnerNo,
          shippingAddress: (
            <>
              <strong>{shippingAddress.partnerName}</strong>
              <br />
              {getFormattedAddress(shippingAddress.address)}
            </>
          )
        }));
    }
  }, [type, listAddress]);

  const columns = useMemo(() => {
    switch (type) {
      case ShippingTypeEnum.TaxInvoices:
        return [
          { field: "BilltoCode", headerName: "Bill-to Code", width: 90 },
          { field: "billingAddress", headerName: "Name", width: 200 }
        ];
      case ShippingTypeEnum.Delivery:
      default:
        return [
          { field: "ShiptoCode", headerName: "Ship-to Code", width: 90 },
          { field: "shippingAddress", headerName: "Name", width: 200 }
        ];
    }
  }, [type]);
  const convertText = e => {
    if (!e?.addressText && e?.displayText) {
      return {
        address: `${e?.displayText}`,
        partnerNo: e?.partnerNo,
        partnerName: e?.partnerName
      };
    }
    if (!e?.addressText && !e?.displayText) {
      return e;
    }

    return {
      address: `${e?.addressText}`,
      partnerNo: e?.partnerNo,
      partnerName: e?.partnerName
    };
  };

  useEffect(() => {
    if (visible) {
      const dataOption = addressOptions?.length ? addressOptions : [];

      const data = dataOption;
      let valueOption = "";
      const newData = (data || []).map(e => convertText(e));
      if (type === ShippingTypeEnum.Point) {
        valueOption = formik.values?.unloadingPoint;
      }
      if (type === ShippingTypeEnum.TaxInvoices) {
        valueOption = formik?.values?.billTo;
      }
      if (type === ShippingTypeEnum.Delivery) {
        valueOption = formik?.values?.shipTo;
      }
      setShipping(valueOption);
      setListAddress(newData);
    }
  }, [visible, addressOptions]);

  useEffect(() => {
    if (type === ShippingTypeEnum.Delivery) {
      const getValue = formik.values?.shipTo;
      setShipping(getValue);
    }
    if (type === ShippingTypeEnum.TaxInvoices) {
      const getValue = formik.values?.billTo;
      setShipping(getValue);
    }
    if (type === ShippingTypeEnum.Point) {
      const getValue = formik.values?.unloadingPoint;
      setShipping(getValue);
    }
  }, [type]);

  const clearSelectVariant = () => {
    onClose();
    setShipping("");
  };

  const dataRenderModal = useMemo(() => {
    switch (type) {
      case ShippingTypeEnum.TaxInvoices:
        return {
          iconHead: InvoiceIcon,
          titleHead: intl.formatMessage({
            defaultMessage: "Bill-to Party",
            description: "label"
          }),
          shippingAddres: address,
          label: intl.formatMessage({
            defaultMessage: "ข้อมูลที่อยู่สำหรับออกใบกำกับภาษี",
            description: "label"
          })
        };
      case ShippingTypeEnum.Point:
        return {
          iconHead: PointIcon,
          titleHead: intl.formatMessage({
            defaultMessage: "เลือกจุดส่งสินค้า",
            description: "label"
          }),
          shippingAddres: address,
          label: intl.formatMessage({
            defaultMessage: "ข้อมูลจุดส่งสินค้า",
            description: "label"
          })
        };

      default:
        return {
          iconHead: ShippingIcon,
          titleHead: intl.formatMessage({
            defaultMessage: "Ship-to Party",
            description: "label"
          }),
          shippingAddres: address,
          label: intl.formatMessage({
            defaultMessage: "ข้อมูลสถานที่จัดส่ง",
            description: "label"
          })
        };
    }
  }, [type]);

  const handleSubmit = () => {
    handleApplyShipping(shipping);
    clearSelectVariant();
  };
  const handleRowSelection = (selectedRowIndex: string) => {
    const selectedItem = listAddress.find(
      list => list.partnerNo === selectedRowIndex
    );
    if (selectedItem) {
      setShipping(selectedItem.address);
    }
  };
  return (
    <>
      <Dialog open={visible} onClose={onClose} className={classes.root}>
        <DialogTitle className={classes.dialogTitle}>
          <h2 className={classes.dialogTitleWrap}>
            <img src={dataRenderModal?.iconHead} alt={type.toString()} />
            <MyTypography variant={"h6"} className={classes.nameHead}>
              {dataRenderModal?.titleHead}&nbsp;
              <span className={classes.rowlength}>({rows.length})</span>
            </MyTypography>
          </h2>
        </DialogTitle>
        <DialogContent dividers={false} className={classes.dialogContent}>
          <Grid container>
            <Grid item xs={12} md={12}>
              <DataTable
                selectedRow={selectedRow}
                setSelectedRow={setSelectedRow}
                headCells={columns}
                rows={rows}
                onRowSelect={handleRowSelection}
                sortField={
                  type === ShippingTypeEnum.TaxInvoices
                    ? "BilltoCode"
                    : "ShiptoCode"
                }
                boldFields={["ShiptoCode", "partnerName", "BilltoCode"]}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant={"outlined"}
            color={"secondary"}
            className={classes.buttonAction}
            onClick={clearSelectVariant}
          >
            <FormattedMessage defaultMessage={"ยกเลิก"} />
          </Button>
          <Button
            variant={"contained"}
            color={"secondary"}
            className={classes.buttonAction}
            disabled={!Boolean(shipping)}
            onClick={handleSubmit}
          >
            <FormattedMessage defaultMessage={"ตกลง"} />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ModalShipping.displayName = "ModalShipping";
export default ModalShipping;
